import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';

import Layout from 'components/Layout';
import HeadWithNoIndexAndNoFollow from 'components/Seo/HeadWithNoIndexAndNoFollow';

import useDialog from 'hooks/useDialog';
import useQueryClient from 'hooks/useQueryClient';

import { toSearchResultsWithCleanedLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { searchQueryKeys } from 'lib/queryKeys';
import { getSearchVertical, sendSupixEvent } from 'lib/supix';

import { useUtm } from 'providers/UtmProvider';

import { getSearchResultsList } from 'services/search';

import SearchResultsBreadcrumb from './Breadcrumb';
import Results from './Results';
import SearchBox from './SearchBox';
import SearchResultsSidebar from './Sidebar';

import LINKS from 'constants/links';
import {
  SEARCH_RESULTS_LEGACY_TYPE_PARAM_TO_TYPE,
  SEARCH_RESULTS_TYPES,
  SEARCH_RESULTS_TYPE_PARAM_TO_COUNT_KEY,
  SEARCH_RESULTS_TYPE_PARAM_TO_TYPE,
  SEARCH_RESULTS_VALID_TYPE_PARAMS,
} from 'constants/searchResults';

const ActionSheet = dynamic(() => import('./ActionSheet'), { ssr: false });

const SearchResultsDialog = dynamic(() => import('./Dialog'), {
  ssr: false,
});

const GA_EVENT_CATEGORY = 'search_page';

const isLegacySearchResultsTypeParamValid = (legacyTypeParam) =>
  Object.keys(SEARCH_RESULTS_LEGACY_TYPE_PARAM_TO_TYPE).some(
    (legacyValidType) => legacyValidType === legacyTypeParam
  );

const isSearchResultsTypeParamValid = (typeParam) =>
  SEARCH_RESULTS_VALID_TYPE_PARAMS.some((validType) => validType === typeParam);

const SearchResults = ({ searchContext = -1 }) => {
  const router = useRouter();
  const { showDialog } = useDialog();
  const { isUtmLoaded } = useUtm();
  const queryClient = useQueryClient();
  const [searchResultsType, setSearchResultsType] = useState(
    SEARCH_RESULTS_TYPES.GENERAL
  );
  const [searchResultsSort, setSearchResultsSort] = useState('');
  const [searchQs, setSearchQs] = useState({
    alias: null,
    sort: null,
    legacySearchResultsTypeParam: null,
    searchResultsPage: null,
    searchQuery: null,
    aliasHash: null,
    searchResultsTypeParam: SEARCH_RESULTS_TYPES.GENERAL.PARAM,
  });

  const {
    data: searchResults,
    isFetching: isFetchingSearchResults,
    isLoading: isLoadingSearchResults,
    isSuccess: isSuccessSearchResults,
  } = useQuery({
    queryKey: searchQueryKeys.list({
      searchQuery: searchQs,
      searchResultsType,
    }),
    queryFn: async ({ signal }) => {
      const result = await getSearchResultsList(
        {
          searchResultsSort: searchQs.sort,
          searchResultsType,
          ...searchQs,
        },
        { signal }
      );

      result.resultCounters = result.info;
      delete result.info;

      return result;
    },
    select: (data) => data ?? {},
    staleTime: Infinity,
  });
  const { searchQuerySuggestedCorrection, resultCounters } =
    searchResults ?? {};

  useEffect(() => {
    const {
      /**
       * Alias is a selection of offers.
       * The request should always have an `alias` (what is the ALIAS_ID)
       * and `v` (what is a validator for that selection)
       * ie.: /buscar?alias=42533&v=475ace99f6caf5feb169403da84c4529
       * For more info, head to:
       * https://www.notion.so/promobit/QA-Para-devs-81a16b0c7b3b4f06abf416111e029702#edba1547aeb3487792e9e70f3bd1b760
       */
      alias = null,
      sort = null,
      a: legacySearchResultsTypeParam = null,
      p: searchResultsPage = null,
      q: searchQuery = null,
      v: aliasHash = null,
      type: searchResultsTypeParam = SEARCH_RESULTS_TYPES.GENERAL.PARAM,
    } = router.query;

    if (!searchQuery && !(alias && aliasHash)) {
      return router.push(LINKS[404]);
    }

    if (legacySearchResultsTypeParam) {
      if (isLegacySearchResultsTypeParamValid(legacySearchResultsTypeParam)) {
        const url = toSearchResultsWithCleanedLink({
          q: searchQuery,
          p: searchResultsPage,
          type: SEARCH_RESULTS_LEGACY_TYPE_PARAM_TO_TYPE[
            legacySearchResultsTypeParam
          ].PARAM,
        });

        return router.push(url);
      }

      return router.push(toSearchResultsWithCleanedLink({ q: searchQuery }));
    }

    const nextSearchQsState = {
      alias,
      sort,
      legacySearchResultsTypeParam,
      searchResultsPage: Number.isInteger(Number(searchResultsPage))
        ? Number(searchResultsPage)
        : null,
      searchQuery,
      aliasHash,
      searchResultsTypeParam,
    };

    if (alias) {
      nextSearchQsState.sort = SEARCH_RESULTS_TYPES.ALIAS.SORT_OPTIONS[1].value;
      nextSearchQsState.searchResultsTypeParam =
        SEARCH_RESULTS_TYPES.ALIAS.PARAM;
    }

    setSearchQs(nextSearchQsState);
  }, [router.query]);

  useEffect(() => {
    const { searchQuery, searchResultsTypeParam, alias, aliasHash } = searchQs;

    const isCurrSearchResultsTypeParamValid = isSearchResultsTypeParamValid(
      searchResultsTypeParam
    );

    if (!isCurrSearchResultsTypeParamValid) {
      router.push(toSearchResultsWithCleanedLink({ q: searchQuery }));
      return;
    }

    if (alias && aliasHash) {
      return setSearchResultsType(SEARCH_RESULTS_TYPES.ALIAS);
    }

    setSearchResultsType(
      SEARCH_RESULTS_TYPE_PARAM_TO_TYPE[searchResultsTypeParam]
    );
  }, [router, searchQs]);

  useEffect(() => {
    const { searchResultsTypeParam, sort } = searchQs;

    if (searchResultsTypeParam !== SEARCH_RESULTS_TYPES.GENERAL.PARAM) {
      /**
       * Validate if sort value is a valid value for the current
       * `searchResultsTypeParam`. Every `searchResultsTypeParam` has its
       * own set of valid sorting values.
       */
      const isSortValueForSearchResultsTypeValid =
        sort &&
        SEARCH_RESULTS_TYPE_PARAM_TO_TYPE[
          searchResultsTypeParam
        ].SORT_OPTIONS.some(({ value }) => sort === value);

      /**
       * Sets `isDefaultOption: true` from `SORT_OPTIONS` as the default
       * sort value if and invalid `sort` is provided.
       */
      const validSort = isSortValueForSearchResultsTypeValid
        ? sort
        : SEARCH_RESULTS_TYPE_PARAM_TO_TYPE[
            searchResultsTypeParam
          ].SORT_OPTIONS.find(({ isDefaultOption }) => isDefaultOption).value;

      return setSearchResultsSort(validSort);
    }

    setSearchResultsSort(sort);
  }, [searchQs]);

  useEffect(() => {
    if (isFetchingSearchResults || isLoadingSearchResults || !resultCounters) {
      return;
    }

    if (isUtmLoaded) {
      const searchVertical = getSearchVertical(searchQs.searchResultsTypeParam);
      const supixParams = { query: searchQs.searchQuery };

      if (searchContext >= 0) {
        supixParams.context = searchContext;
      }

      if (searchVertical > 0) {
        supixParams.vertical = searchVertical;
      }

      switch (searchVertical) {
        case 1:
          supixParams.results = resultCounters.activeOffers;
          break;
        case 2:
          supixParams.results = resultCounters.finishedOffers;
          break;
        case 3:
          supixParams.results = resultCounters.coupons;
          break;
        case 4:
          supixParams.results = resultCounters.threads;
          break;
        case 5:
          supixParams.results = resultCounters.users;
          break;
        case 6:
          supixParams.results = resultCounters.articles;
          break;
        default:
          supixParams.results = resultCounters.total;
      }

      sendSupixEvent('/ser', supixParams);
    }
  }, [
    searchQs.searchQuery,
    resultCounters?.total,
    searchContext,
    isUtmLoaded,
    isFetchingSearchResults,
    isLoadingSearchResults,
  ]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries(
        searchQueryKeys.list({ searchQuery: searchQs, searchResultsType })
      );
    };
  }, []);

  const hasResults = isSuccessSearchResults
    ? searchResults.resultCounters?.total > 0 ||
      /**
       * If `alias` and `aliasHash` are used instead of `searchQuery`,
       * a different kind of data structure comes from the API..
       */
      searchResults.offers?.length > 0
    : false;
  const hasBreadcrumb =
    searchQs.searchQuery &&
    isSearchResultsTypeParamValid(searchQs.searchResultsTypeParam) &&
    hasResults;
  const isLoading = isLoadingSearchResults || isFetchingSearchResults;
  const hasFilters = !searchQs.alias && hasResults;

  const onFilterButtonClick = useCallback(
    () =>
      showDialog(SearchResultsDialog, {
        resultCounters,
        searchQuery: searchQs.searchQuery,
        isLoadingResults: isLoading,
        hasFilters,
        searchResultsType,
        gaEventCategory: GA_EVENT_CATEGORY,
      }),
    [isLoading, hasFilters, searchQs.searchQuery, resultCounters]
  );

  return (
    <>
      <HeadWithNoIndexAndNoFollow />
      <Layout
        gaEventCategory={GA_EVENT_CATEGORY}
        gridProps={{ className: 'pt-6 mb-4' }}
        sidebarComponent={
          <SearchResultsSidebar
            gaEventCategory={GA_EVENT_CATEGORY}
            hasFilters={hasFilters}
            isLoadingResults={isLoading}
            resultCounters={resultCounters}
            searchQuery={searchQs.searchQuery}
            searchResultsType={searchResultsType}
          />
        }
        maxWidthOnMobileDevices
        scrollToTopButton
      >
        {hasBreadcrumb && (
          <SearchResultsBreadcrumb
            queryTypeName={searchResultsType?.NAME}
            searchQuery={searchQs.searchQuery}
          />
        )}
        <SearchBox
          gaEventCategory={GA_EVENT_CATEGORY}
          hasResults={hasResults && !searchQs.alias}
          searchQuerySuggestedCorrection={searchQuerySuggestedCorrection}
          searchResultsCount={
            resultCounters?.[
              SEARCH_RESULTS_TYPE_PARAM_TO_COUNT_KEY?.[searchResultsType?.PARAM]
            ]
          }
          searchResultsType={
            searchQs.alias ? SEARCH_RESULTS_TYPES.GENERAL : searchResultsType
          }
        />
        {!isLoading && hasResults && hasFilters && (
          <Box
            className="flex items-end rounded-b-2 rounded-t-none px-4 pb-4 pt-0 md:pt-2 xl:hidden"
            full
          >
            <Button
              iconLeft={<Icon name="sliders" />}
              size="size3"
              onClick={onFilterButtonClick}
            >
              {placeholder('actions.showFilters')}
            </Button>
          </Box>
        )}
        <Results
          gaEventCategory={GA_EVENT_CATEGORY}
          hasResults={hasResults}
          isLoading={isLoading}
          searchQs={searchQs}
          searchResults={searchResults}
          searchResultsSort={searchResultsSort}
          searchResultsType={searchResultsType}
          onFilterButtonClick={onFilterButtonClick}
        />
        {(!hasResults ||
          searchResultsType?.PARAM ===
            SEARCH_RESULTS_TYPES.FINISHED_OFFERS.PARAM) && (
          <ActionSheet
            mobileScrollPositionToHide={!hasResults ? 1500 : 3600}
            mostRelevantKeyword={searchQs.searchQuery}
            photo={searchResults?.offers?.[0]?.offerPhoto}
            scrollPositionToHide={!hasResults ? 1000 : 2300}
          />
        )}
      </Layout>
    </>
  );
};

SearchResults.propTypes = {
  searchContext: PropTypes.number,
};

export default SearchResults;
